import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ROLES } from "@/lib/constants";
import { toast } from "sonner";
import { ShieldCheck, GraduationCap } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "contributor" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
      toast.success("Welcome");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-indigo-950 via-indigo-900 to-slate-900 text-white">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-white/10 border border-white/20 grid place-items-center"><ShieldCheck className="h-5 w-5" /></div>
          <div>
            <div className="font-bold tracking-tight text-lg" style={{ fontFamily: "Plus Jakarta Sans" }}>QIP Studio</div>
            <div className="text-xs text-indigo-200">Ofsted-aligned quality improvement</div>
          </div>
        </div>
        <div className="max-w-md">
          <div className="text-xs uppercase tracking-widest text-indigo-300 mb-4">FE & Skills · Toolkit v2.0 · Sept 2026</div>
          <h1 className="text-4xl font-extrabold leading-tight" style={{ fontFamily: "Plus Jakarta Sans" }}>Evidence-led self-assessment, ready for inspection.</h1>
          <p className="mt-4 text-indigo-200 leading-relaxed">A single workspace to plan improvement, curate evidence and draft your SAR against all seven Ofsted evaluation areas.</p>
          <div className="grid grid-cols-3 gap-3 mt-8">
            {["Safeguarding", "Inclusion", "Leadership", "Skills Needs", "Curriculum", "Achievement", "Participation"].map((k) => (
              <div key={k} className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium">{k}</div>
            ))}
          </div>
        </div>
        <div className="text-xs text-indigo-300">© 2026 QIP Studio</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <Card className="w-full max-w-md p-8 shadow-sm">
          <div className="flex items-center gap-2 mb-6"><GraduationCap className="h-5 w-5 text-indigo-800" /><span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{mode === "login" ? "Sign in" : "Create account"}</span></div>
          <h2 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Plus Jakarta Sans" }}>{mode === "login" ? "Welcome back" : "Join your provider"}</h2>
          <p className="text-sm text-slate-500 mb-6">{mode === "login" ? "Use your provider credentials to continue." : "Set up access to your QIP workspace."}</p>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input data-testid="reg-name" id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input data-testid="login-email" id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input data-testid="login-password" id="password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            {mode === "register" && (
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="reg-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => (<SelectItem key={r.key} value={r.key} data-testid={`role-opt-${r.key}`}>{r.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            )}
            <Button data-testid="submit-auth" type="submit" disabled={busy} className="w-full bg-indigo-900 hover:bg-indigo-950 text-white">{busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}</Button>
          </form>
          <button data-testid="toggle-auth-mode" type="button" onClick={() => setMode(mode === "login" ? "register" : "login")} className="mt-4 text-sm text-indigo-800 hover:underline">
            {mode === "login" ? "Need an account? Register" : "Have an account? Sign in"}
          </button>
        </Card>
      </div>
    </div>
  );
}
